import React, { useState } from 'react';
import { getMcpServerUrl } from '../lib/mcpServerUrl';
import ChatExample, { ClientId } from './ChatExample';

interface ClientInfo {
  label: string;
  settingsPath: string;
  addLabel: string;
  note?: string;
  extraStep?: string;
}

const CLIENTS: Record<ClientId, ClientInfo> = {
  claude: {
    label: 'Claude',
    settingsPath: 'Settings → Connectors',
    addLabel: 'Add custom connector',
  },
  chatgpt: {
    label: 'ChatGPT',
    settingsPath: 'Settings → Connectors',
    addLabel: 'Add connector',
  },
  gemini: {
    label: 'Gemini',
    settingsPath: 'Settings & help → Connected Apps, under "Custom apps for Spark"',
    addLabel: 'Add a custom app',
    note: 'Gemini’s custom connectors ("Gemini Spark") currently require a personal Google account, are limited to users 18+ in the US, and only work in the gemini.google.com web/mobile app.',
    extraStep: 'To use Stashr in a chat, type @ and select it from the list — unlike Claude and ChatGPT, Gemini doesn’t use connected apps automatically.',
  },
};

const CopyUrlBox: React.FC<{ url: string }> = ({ url }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={styles.urlBox}>
      <code style={styles.urlText}>{url}</code>
      <button style={styles.copyButton} onClick={handleCopy}>
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '2rem 0 4rem',
    width: '100%',
  },
  intro: {
    maxWidth: '32rem',
    textAlign: 'center',
    marginBottom: '2rem',
  },
  tabBar: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
  },
  tab: {
    padding: '0.625rem 1.5rem',
    borderRadius: 'var(--border-radius)',
    border: '1px solid transparent',
    background: 'transparent',
    color: 'var(--text-color-muted)',
    fontSize: '0.9375rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  tabActive: {
    background: 'var(--primary-color)',
    color: 'var(--white-color)',
  },
  panel: {
    width: '100%',
    maxWidth: '32rem',
    background: 'var(--white-color)',
    borderRadius: '1rem',
    boxShadow: '0 4px 24px rgba(45, 52, 54, 0.08)',
    padding: '2rem',
  },
  steps: {
    margin: 0,
    padding: '0 0 0 1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  urlBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: '#F5F7FA',
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    margin: '0.5rem 0',
  },
  urlText: {
    flex: 1,
    fontSize: '0.875rem',
    color: 'var(--primary-text-color)',
    overflowWrap: 'anywhere',
  },
  copyButton: {
    flexShrink: 0,
    border: 'none',
    borderRadius: '0.375rem',
    background: 'var(--primary-color)',
    color: 'var(--white-color)',
    padding: '0.375rem 0.875rem',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  note: {
    marginTop: '1.5rem',
    padding: '0.75rem 1rem',
    background: '#FFF9E6',
    border: '1px solid var(--accent-color)',
    borderRadius: '0.5rem',
    fontSize: '0.8125rem',
    color: 'var(--text-color-muted)',
  },
};

const MCPLanding: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ClientId>('claude');
  const mcpServerUrl = getMcpServerUrl();
  const client = CLIENTS[activeTab];

  return (
    <div style={styles.page}>
      <div style={styles.intro}>
        <h1>Connect Stashr</h1>
        <p style={{ marginTop: '0.75rem' }}>
          Get card recommendations right inside Claude, ChatGPT, or Gemini — no separate app to
          open. Takes under a minute to connect.
        </p>
      </div>

      <div style={styles.tabBar} role="tablist">
        {(Object.keys(CLIENTS) as ClientId[]).map((id) => (
          <button
            key={id}
            role="tab"
            aria-selected={activeTab === id}
            style={{ ...styles.tab, ...(activeTab === id ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(id)}
          >
            {CLIENTS[id].label}
          </button>
        ))}
      </div>

      <div style={styles.panel} role="tabpanel">
        <ol style={styles.steps}>
          <li>
            In {client.label}, go to <strong>{client.settingsPath}</strong>.
          </li>
          <li>
            Click <strong>{client.addLabel}</strong>.
          </li>
          <li>
            Paste this URL:
            <CopyUrlBox url={mcpServerUrl} />
          </li>
          <li>
            Sign in with Google when prompted.
            {client.extraStep && <> {client.extraStep}</>}
          </li>
        </ol>

        {client.note && <div style={styles.note}>{client.note}</div>}
      </div>

      <ChatExample client={activeTab} style={{ marginTop: '3rem' }} />
    </div>
  );
};

export default MCPLanding;
